import { alpha, Box, Button, InputAdornment, styled, TableCell, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import Dinero from 'dinero.js';
import { ChangeEvent, FocusEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type Props = {
	name: string
	description?: string
	price: Dinero.Dinero,
	defaultPrice: Dinero.Dinero
	isCustom: boolean
	costPrice: Dinero.Dinero,
	onPriceChange: (price: Dinero.Dinero) => void
	onReset: () => void
	minimumPriceAmount?: number
}

const StyledCell = styled(TableCell)(({ theme }) => ({
	backgroundColor: '#fff',
	borderLeft: '1px solid #cbecf8',
	borderRight: '1px solid #cbecf8',
	borderBottom: '1px solid #cbecf8',
	fontSize: '1rem',

	'&:first-of-type': {
		borderLeft: 0,
	},
	'&:last-of-type': {
		borderRight: 0,
	}
}))

const StyledInputCell = styled(StyledCell)(({ theme }) => ({
	padding: theme.spacing(1, 2),

	'p': {
		margin: 0,
	}
}))

const StyledRow = styled(TableRow)(({ theme }) => ({
	'tbody:last-of-type > &:last-of-type': {
		[`${StyledCell}`]: {
			borderBottom: 0,
		},
		[`${StyledInputCell}`]: {
			borderBottom: 0,
		}
	}
}))

type PriceTextFieldProps = {
	priceError?: boolean
}

const PriceTextField = styled(TextField, {
	shouldForwardProp: prop => prop !== 'priceError',
})<PriceTextFieldProps>(({ priceError, theme }) => ({
	...(priceError && {
		'.MuiInput-root': {
			backgroundColor: alpha(theme.palette.error.main, 0.08),
			boxShadow: `inset 0 0 0 2px ${alpha(theme.palette.error.main, 0.72)}`,
		},

		'.MuiInput-root:not(.Mui-disabled):hover, .MuiInput-root.Mui-focused': {
			backgroundColor: alpha(theme.palette.error.main, 0.12),
		},

		'.MuiInputAdornment-root, .MuiInput-input': {
			color: theme.palette.error.dark,
		},
	}),
}))

export default function PriceRow(props: Props) {
	const [t] = useTranslation('account-v2');
	const [priceInput, setPriceInput] = useState(props.price.toRoundedUnit(2).toFixed(2));
	const [profitInput, setProfitInput] = useState(props.price.subtract(props.costPrice).toRoundedUnit(2).toFixed(2));

	useEffect(() => {
		setPriceInput(props.price.toRoundedUnit(2).toFixed(2))
		setProfitInput(props.price.subtract(props.costPrice).toRoundedUnit(2).toFixed(2))
	}, [props.price, props.costPrice])

	const price = useMemo(() => {
		let parsedInput = parseFloat(priceInput);
		parsedInput = isNaN(parsedInput) ? 0 : Math.round(parsedInput * 100);

		return Dinero({ amount: parsedInput, currency: props.price.getCurrency() })
	}, [priceInput, props.price]);

	const profit = useMemo(() => {
		let parsedInput = parseFloat(profitInput);
		parsedInput = isNaN(parsedInput) ? 0 : Math.round(parsedInput * 100);

		return Dinero({ amount: parsedInput, currency: props.price.getCurrency() })
	}, [profitInput, props.price]);

	const priceHasError = useMemo(() => {
		return props.minimumPriceAmount !== undefined && price.getAmount() < props.minimumPriceAmount;
	}, [price, props.minimumPriceAmount]);
	const isCustom = props.isCustom || !price.equalsTo(props.defaultPrice);

	const onPriceChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setPriceInput(e.target.value)
	}, [])

	const onPriceBlur = useCallback((e: FocusEvent<HTMLInputElement>) => {
		setPriceInput(price.toRoundedUnit(2).toFixed(2))

		if (!price.equalsTo(props.price)) props.onPriceChange(price)
	}, [price, props.price, props.onPriceChange])

	const onProfitChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setProfitInput(e.target.value)
	}, [])

	const onProfitBlur = useCallback((e: FocusEvent<HTMLInputElement>) => {
		setProfitInput(profit.toRoundedUnit(2).toFixed(2))

		const price = props.costPrice.add(profit);
		if (!price.equalsTo(props.price)) props.onPriceChange(price)
	}, [profit, props.costPrice, props.price, props.onPriceChange])

	return <StyledRow>
		<StyledCell sx={{minWidth: 100}}>
			{props.name}
			{props.description ? <Typography variant="caption" component="div" color="text.secondary">{props.description}</Typography> : null}
		</StyledCell>
		<StyledInputCell width={254}>
			<Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(140px, 1fr) 72px', gap: 1, alignItems: 'center' }}>
				<PriceTextField
					fullWidth
					type="number"
					error={priceHasError}
					priceError={priceHasError}
					inputProps={{
						min: props.minimumPriceAmount !== undefined ? props.minimumPriceAmount / 100 : 0,
						step: 0.01,
					}}
					InputProps={{
						startAdornment: <InputAdornment position="start">$</InputAdornment>,
					}}
					value={priceInput}
					onChange={onPriceChange}
					onBlur={onPriceBlur}
				/>
				<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
					{isCustom ? <Tooltip title={t('Custom')}>
						<Button
							variant="outlined"
							size="small"
							onClick={props.onReset}
							aria-label={t('Reset {{name}} to default price', { name: props.name })}
							sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: '0.75rem', textTransform: 'none' }}
						>
							{t('Reset')}
						</Button>
					</Tooltip> : <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{t('Default')}</Typography>}
				</Box>
			</Box>
		</StyledInputCell>
		<StyledCell width={120}>{props.costPrice.toFormat()}</StyledCell>
		<StyledInputCell width={174}>
			<TextField
				type="number"
				inputProps={{
					min: 0,
					step: 0.01,
				}}
				InputProps={{
					startAdornment: <InputAdornment position="start">$</InputAdornment>
				}}
				value={profitInput}
				onChange={onProfitChange}
				onBlur={onProfitBlur}
			/>
		</StyledInputCell>
	</StyledRow>
}
